/**
 * The signed-out page at `/`.
 *
 * Everything on it is a claim about what rypt does with key material, so it is written
 * once, here, and never assembled from fragments at a call site.
 */
export const landing = {
  wordmark: 'rypt',
  display: 'Encryption as a service, in front of Google Cloud KMS.',
  prose:
    'Key material lives in the operator’s Google Cloud project and never reaches you. Your tenant gets an API token, five cryptographic operations, and an audit log that records every one of them. Signing in creates your tenant; the first encrypted round-trip takes about ninety seconds after that.',
  signIn: 'Sign in',
  signingIn: 'Opening Auth0',
  caption: 'First sign-in creates your tenant and makes you its owner.',
  footer: 'API.RYPT.DEV · OPENAPI 3.0.3',
} as const;

/**
 * Auth0 rejects a sign-in by sending the user back to the callback with `error` and
 * `error_description` in the query string. Those two land here, not in the API error
 * table: no rypt request was ever made, so there is no request id and no rypt error code.
 *
 * `access_denied` and an unverified email are the two a real person actually hits, and
 * each has a different fix. Everything else falls through to the generic line, which
 * still names the code rather than hiding it.
 */
export function auth0Error(error: string, description?: string | null): string {
  const detail = description?.trim().replace(/\.$/, '');
  switch (error) {
    case 'access_denied':
      return `access_denied: Auth0 refused this sign-in. ${detail ?? 'No reason was given'}. Sign in again, or use another account.`;
    case 'unauthorized':
      return 'unauthorized: Auth0 will not issue a token until this address is verified. Open the message Auth0 sent you and follow the link, then sign in again.';
    default:
      return `${error}: Auth0 stopped the sign-in. ${detail ?? 'No reason was given'}. Sign in again.`;
  }
}
